// Accessible paywall / upgrade overlay for the commercial layer.
//
// Listens for the `joeflow:require-upgrade` CustomEvent that the
// CommerceController dispatches (out of credits, premium effect, watermark
// removal, or a manual upgrade tap) and renders a WCAG-friendly modal:
//   - role="dialog" + aria-modal + labelled by the heading
//   - focus trap, ESC to close, focus restored to the trigger on close
//   - respects prefers-reduced-motion
//   - includes the legally required disclosures (AI Act Art. 50, FAGG
//     withdrawal waiver, price transparency).
//
// Pure DOM (no framework). All side-effects are injectable so it is unit-tested
// under jsdom without touching the real CommerceController.

import type { Tier } from '../domain/types';
import { TIERS } from '../domain/tiers';
import type { PaywallReason } from '../bridge/commerce-controller';
import { trapBackground } from './overlay-a11y';

export type BillingPeriod = 'monthly' | 'annual';

export interface PaywallView {
  founderActive: boolean;
  founderRemaining: number;
}

export interface PaywallDeps {
  /** Current commerce state snapshot (drives founder pricing). */
  getState: () => PaywallView;
  /** Invoked when the user picks a tier. */
  onSelectTier: (tier: Tier, period: BillingPeriod) => unknown;
  /** Optional "restore purchases" handler. */
  onRestore?: () => unknown;
  /**
   * Optional one-click MOCK "Stripe" credit top-up (TEST ONLY). Shown for the
   * out-of-credits / manual reasons. Resolves on success → the paywall closes.
   */
  onTopUp?: () => Promise<unknown>;
  /** DOM document (default: global document). */
  doc?: Document;
  /** Event target to listen on (default: global window). */
  target?: EventTarget;
  /** Reduced-motion check (default: matchMedia, jsdom-safe). */
  prefersReducedMotion?: () => boolean;
}

export interface PaywallHandle {
  open: (reason: PaywallReason) => void;
  close: () => void;
  destroy: () => void;
  readonly isOpen: boolean;
}

const TIERS_SHOWN: readonly Tier[] = ['starter', 'creator'];

const REASON_HEADING: Record<PaywallReason, string> = {
  out_of_credits: 'Keine KI-Credits mehr',
  premium_effect: 'Premium-Effekt freischalten',
  no_watermark: 'Wasserzeichen entfernen',
  manual: 'JOE FLOW Pro freischalten',
};

const REASON_SUB: Record<PaywallReason, string> = {
  out_of_credits: 'Hol dir frische KI-Credits und zeichne ohne Limit weiter.',
  premium_effect: 'Dieser Effekt ist Teil der Premium-Werkzeuge.',
  no_watermark: 'Exportiere deine Werke in voller Qualität ohne Wasserzeichen.',
  manual: 'Mehr Credits, alle Effekte und Export ohne Wasserzeichen.',
};

function eur(value: number): string {
  return value.toLocaleString('de-AT', { style: 'currency', currency: 'EUR' });
}

function defaultReducedMotion(): boolean {
  try {
    return (
      typeof globalThis.matchMedia === 'function' &&
      globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  } catch {
    return false;
  }
}

const STYLE_ID = 'jf-paywall-style';
const STYLES = `
.jf-paywall-overlay{position:fixed;inset:0;z-index:2000;display:flex;align-items:center;
  justify-content:center;padding:16px;background:rgba(15,23,42,.72);backdrop-filter:blur(4px);}
.jf-paywall-overlay[hidden]{display:none;}
.jf-paywall-dialog{background:#1e293b;color:#f1f5f9;width:min(560px,100%);max-height:90vh;
  overflow:auto;border-radius:16px;padding:24px;box-shadow:0 20px 60px rgba(0,0,0,.5);
  font-family:system-ui,-apple-system,sans-serif;}
.jf-paywall-anim{animation:jf-paywall-in .18s ease-out;}
@keyframes jf-paywall-in{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:none;}}
.jf-paywall-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;}
.jf-paywall-title{font-size:1.35rem;font-weight:700;margin:0;}
.jf-paywall-sub{margin:4px 0 16px;color:#cbd5e1;font-size:.95rem;}
.jf-paywall-close{background:transparent;border:0;color:#cbd5e1;font-size:1.5rem;line-height:1;
  cursor:pointer;min-width:44px;min-height:44px;border-radius:8px;}
.jf-paywall-close:hover,.jf-paywall-close:focus-visible{background:rgba(255,255,255,.1);color:#fff;}
.jf-paywall-periods{display:flex;gap:8px;margin-bottom:16px;}
.jf-paywall-period{flex:1;min-height:44px;border:1px solid #334155;background:#0f172a;color:#cbd5e1;
  border-radius:10px;cursor:pointer;font-size:.9rem;}
.jf-paywall-period[aria-pressed="true"]{background:#22d3ee;color:#08222b;border-color:#22d3ee;font-weight:700;}
.jf-paywall-tiers{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
@media(max-width:460px){.jf-paywall-tiers{grid-template-columns:1fr;}}
.jf-paywall-card{position:relative;border:1px solid #334155;border-radius:12px;padding:16px;display:flex;flex-direction:column;gap:8px;}
.jf-paywall-card-popular{border-color:#22d3ee;box-shadow:0 0 0 1px #22d3ee;}
.jf-paywall-badge{position:absolute;top:-10px;right:12px;background:#22d3ee;color:#08222b;
  font:700 11px system-ui,sans-serif;padding:2px 8px;border-radius:999px;}
.jf-paywall-save{color:#34d399;font-size:.8rem;font-weight:600;}
.jf-paywall-card h3{margin:0;font-size:1.05rem;}
.jf-paywall-price{font-size:1.5rem;font-weight:800;}
.jf-paywall-price small{font-size:.8rem;font-weight:500;color:#94a3b8;}
.jf-paywall-anchor{color:#94a3b8;text-decoration:line-through;font-size:.85rem;}
.jf-paywall-buy{margin-top:auto;min-height:44px;border:0;border-radius:10px;background:#e67e22;
  color:#fff;font-weight:700;cursor:pointer;font-size:.95rem;}
.jf-paywall-buy:hover,.jf-paywall-buy:focus-visible{background:#d35400;}
.jf-paywall-founder{margin-top:14px;font-size:.85rem;color:#fcd34d;}
.jf-paywall-topup{margin-top:14px;width:100%;min-height:48px;border:0;border-radius:10px;
  background:#635bff;color:#fff;font-weight:700;cursor:pointer;font-size:.95rem;}
.jf-paywall-topup:hover,.jf-paywall-topup:focus-visible{background:#534dd8;}
.jf-paywall-topup[aria-disabled="true"]{opacity:.7;cursor:progress;}
.jf-paywall-topup-note{margin:6px 0 0;color:#94a3b8;font-size:.74rem;text-align:center;}
.jf-paywall-restore{margin-top:12px;background:transparent;border:0;color:#22d3ee;cursor:pointer;
  min-height:44px;font-size:.9rem;text-decoration:underline;}
.jf-paywall-legal{margin-top:16px;padding-top:12px;border-top:1px solid #334155;color:#94a3b8;
  font-size:.72rem;line-height:1.5;}
@media(prefers-reduced-motion:reduce){.jf-paywall-anim{animation:none;}}
`;

const FOCUSABLE =
  'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';

export function mountPaywall(deps: PaywallDeps): PaywallHandle {
  const doc = deps.doc ?? document;
  const target = deps.target ?? window;
  const reducedMotion = deps.prefersReducedMotion ?? defaultReducedMotion;

  let overlay: HTMLElement | null = null;
  let lastFocused: Element | null = null;
  let period: BillingPeriod = 'annual'; // PRD §3: annual is the marked default
  let open = false;
  let releaseBackground: (() => void) | null = null;

  function ensureStyle(): void {
    if (doc.getElementById(STYLE_ID)) return;
    const style = doc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLES;
    (doc.head ?? doc.body ?? doc.documentElement).appendChild(style);
  }

  function priceFor(tier: Tier): { now: number; anchor: number | null } {
    const spec = TIERS[tier];
    const state = deps.getState();
    const founder = state.founderActive;
    if (period === 'annual') {
      const f = spec.founderAnnual;
      return founder && f != null
        ? { now: f, anchor: spec.regularAnnual }
        : { now: spec.regularAnnual, anchor: null };
    }
    const f = spec.founderMonthly;
    return founder && f != null
      ? { now: f, anchor: spec.regularMonthly }
      : { now: spec.regularMonthly, anchor: null };
  }

  /** % saved by paying annually vs monthly×12 (founder-aware, displayed prices). */
  function annualSavingsPercent(tier: Tier): number {
    const spec = TIERS[tier];
    const founder = deps.getState().founderActive;
    const monthly =
      founder && spec.founderMonthly != null ? spec.founderMonthly : spec.regularMonthly;
    const annual = founder && spec.founderAnnual != null ? spec.founderAnnual : spec.regularAnnual;
    if (monthly <= 0 || annual <= 0) return 0;
    return Math.max(0, Math.round((1 - annual / (monthly * 12)) * 100));
  }

  function render(reason: PaywallReason): HTMLElement {
    const state = deps.getState();
    const titleId = 'jf-paywall-title';
    const descId = 'jf-paywall-desc';

    const ov = doc.createElement('div');
    ov.className = 'jf-paywall-overlay';
    ov.dataset.reason = reason;

    const dialog = doc.createElement('div');
    dialog.className = 'jf-paywall-dialog' + (reducedMotion() ? '' : ' jf-paywall-anim');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', titleId);
    dialog.setAttribute('aria-describedby', descId);

    const periodLabel = period === 'annual' ? '/Jahr' : '/Monat';
    const tierCards = TIERS_SHOWN.map((tier) => {
      const { now, anchor } = priceFor(tier);
      const grant = TIERS[tier].creditGrant;
      const name = tier === 'starter' ? 'Starter' : 'Creator';
      const popular = tier === 'creator'; // PRD §3: "beliebt"-Badge auf Creator
      const credits =
        grant.period === 'daily'
          ? `${grant.amount} KI-Credits / Tag`
          : `${grant.amount} KI-Credits / Monat`;
      const save = period === 'annual' ? annualSavingsPercent(tier) : 0;
      return `
        <div class="jf-paywall-card${popular ? ' jf-paywall-card-popular' : ''}">
          ${popular ? '<span class="jf-paywall-badge">Beliebt</span>' : ''}
          <h3>${name}</h3>
          <div class="jf-paywall-price">${eur(now)}<small>${periodLabel}</small></div>
          ${anchor != null ? `<div class="jf-paywall-anchor">${eur(anchor)}${periodLabel}</div>` : ''}
          ${save > 0 ? `<div class="jf-paywall-save">${save}% sparen ggü. monatlich</div>` : ''}
          <div>${credits}</div>
          <button type="button" class="jf-paywall-buy" data-tier="${tier}">${name} wählen</button>
        </div>`;
    }).join('');

    const founderNote =
      state.founderActive && state.founderRemaining > 0
        ? `<p class="jf-paywall-founder">⭐ Founder-Preis — nur noch ${state.founderRemaining} Plätze.</p>`
        : '';

    // Mock "Stripe" top-up — only for the credit-related reasons and only when
    // a handler is wired. One click confirms the (mock) payment and recharges.
    const showTopUp = !!deps.onTopUp && (reason === 'out_of_credits' || reason === 'manual');
    const topUpBlock = showTopUp
      ? `<button type="button" class="jf-paywall-topup">Mit Stripe 50 Credits kaufen</button>
        <p class="jf-paywall-topup-note">Testmodus — es wird keine echte Zahlung ausgelöst.</p>`
      : '';

    dialog.innerHTML = `
      <div class="jf-paywall-head">
        <h2 class="jf-paywall-title" id="${titleId}">${REASON_HEADING[reason]}</h2>
        <button type="button" class="jf-paywall-close" aria-label="Schließen">&times;</button>
      </div>
      <p class="jf-paywall-sub" id="${descId}">${REASON_SUB[reason]}</p>
      <div class="jf-paywall-periods" role="group" aria-label="Abrechnungszeitraum">
        <button type="button" class="jf-paywall-period" data-period="monthly" aria-pressed="${period === 'monthly'}">Monatlich</button>
        <button type="button" class="jf-paywall-period" data-period="annual" aria-pressed="${period === 'annual'}">Jährlich</button>
      </div>
      <div class="jf-paywall-tiers">${tierCards}</div>
      ${founderNote}
      ${topUpBlock}
      <button type="button" class="jf-paywall-restore">Käufe wiederherstellen</button>
      <div class="jf-paywall-legal">
        <p>KI-Hinweis (EU AI Act Art. 50): Mit JOE FLOW AI erzeugte Bilder werden als KI-generiert gekennzeichnet.</p>
        <p>Digitale Inhalte: Mit dem Kauf stimmst du der sofortigen Bereitstellung zu und verlierst dein 14-tägiges Widerrufsrecht (§ 18 Abs. 1 Z 11 FAGG).</p>
        <p>Alle Preise inkl. USt. Das Abo verlängert sich automatisch und ist jederzeit kündbar.</p>
      </div>`;

    ov.appendChild(dialog);
    return ov;
  }

  function trapFocus(e: KeyboardEvent): void {
    if (!overlay) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusables = Array.from(overlay.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = doc.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onClick(e: Event): void {
    const t = e.target as HTMLElement;
    if (t.classList.contains('jf-paywall-overlay')) return close(); // backdrop
    if (t.classList.contains('jf-paywall-close')) return close();
    const periodBtn = t.closest<HTMLElement>('.jf-paywall-period');
    if (periodBtn?.dataset.period) {
      period = periodBtn.dataset.period as BillingPeriod;
      rerender();
      return;
    }
    const buyBtn = t.closest<HTMLElement>('.jf-paywall-buy');
    if (buyBtn?.dataset.tier) {
      deps.onSelectTier(buyBtn.dataset.tier as Tier, period);
      return;
    }
    const topUpBtn = t.closest<HTMLElement>('.jf-paywall-topup');
    if (topUpBtn) {
      // Latch with aria-disabled (not [disabled]) so the button stays in the
      // focus order and tests can still query it — guarantees a single submit.
      if (topUpBtn.getAttribute('aria-disabled') === 'true') return;
      topUpBtn.setAttribute('aria-disabled', 'true');
      topUpBtn.textContent = 'Zahlung wird bestätigt…';
      Promise.resolve(deps.onTopUp?.())
        .then(() => close()) // success → close, the user continues generating
        .catch(() => {
          topUpBtn.removeAttribute('aria-disabled');
          topUpBtn.textContent = 'Erneut versuchen';
        });
      return;
    }
    if (t.classList.contains('jf-paywall-restore')) {
      deps.onRestore?.();
    }
  }

  function rerender(): void {
    if (!overlay) return;
    const reason = (overlay.dataset.reason as PaywallReason) ?? 'manual';
    const fresh = render(reason);
    overlay.replaceWith(fresh);
    overlay = fresh;
    wire(fresh);
    fresh.querySelector<HTMLElement>('.jf-paywall-period[aria-pressed="true"]')?.focus();
  }

  function wire(ov: HTMLElement): void {
    ov.addEventListener('click', onClick);
  }

  function close(): void {
    if (!overlay) return;
    overlay.removeEventListener('click', onClick);
    overlay.remove();
    overlay = null;
    open = false;
    releaseBackground?.();
    releaseBackground = null;
    doc.removeEventListener('keydown', trapFocus, true);
    if (lastFocused instanceof HTMLElement) lastFocused.focus();
  }

  function openPaywall(reason: PaywallReason): void {
    if (overlay) close();
    ensureStyle();
    lastFocused = doc.activeElement;
    overlay = render(reason);
    (doc.body ?? doc.documentElement).appendChild(overlay);
    releaseBackground = trapBackground(overlay);
    wire(overlay);
    open = true;
    doc.addEventListener('keydown', trapFocus, true);
    overlay.querySelector<HTMLElement>('.jf-paywall-close')?.focus();
  }

  function onRequireUpgrade(e: Event): void {
    const detail = (e as CustomEvent<{ reason?: PaywallReason }>).detail;
    openPaywall(detail?.reason ?? 'manual');
  }

  target.addEventListener('joeflow:require-upgrade', onRequireUpgrade as EventListener);

  return {
    open: openPaywall,
    close,
    destroy: () => {
      close();
      target.removeEventListener('joeflow:require-upgrade', onRequireUpgrade as EventListener);
    },
    get isOpen() {
      return open;
    },
  };
}
